import React from "react";

/**
 * A styled banner used as a card thumbnail when there is no real image
 * available (releases, product sheets, documents — i.e. files, not pictures).
 *
 * It renders a large file-type icon + the extension label over a colour that
 * is derived from the file type, so every card looks intentional even without
 * an uploaded cover image.
 */

type FileStyle = {
  icon: string; // FontAwesome class
  label: string; // short label shown under the icon, e.g. "PDF"
  iconColor: string; // tailwind text color for the icon (subtle accent only)
};

// All tiles share the dark sidebar background (bg-gray-900). The file type is
// conveyed by the icon + label and a soft accent colour on the icon, not by the
// whole banner — so it reads as part of the same dark theme as the sidebar.
const TILE_GRADIENT = "from-gray-800 to-gray-900";

const DEFAULT_STYLE: FileStyle = {
  icon: "fa-solid fa-file",
  label: "FILE",
  iconColor: "text-gray-300",
};

// Map a normalized extension -> styling.
const STYLES: Record<string, FileStyle> = {
  pdf: { icon: "fa-solid fa-file-pdf", label: "PDF", iconColor: "text-rose-300" },
  zip: { icon: "fa-solid fa-file-zipper", label: "ZIP", iconColor: "text-amber-300" },
  rar: { icon: "fa-solid fa-file-zipper", label: "RAR", iconColor: "text-amber-300" },
  doc: { icon: "fa-solid fa-file-word", label: "DOC", iconColor: "text-blue-300" },
  docx: { icon: "fa-solid fa-file-word", label: "DOCX", iconColor: "text-blue-300" },
  xls: { icon: "fa-solid fa-file-excel", label: "XLS", iconColor: "text-emerald-300" },
  xlsx: { icon: "fa-solid fa-file-excel", label: "XLSX", iconColor: "text-emerald-300" },
  csv: { icon: "fa-solid fa-file-csv", label: "CSV", iconColor: "text-emerald-300" },
  ppt: { icon: "fa-solid fa-file-powerpoint", label: "PPT", iconColor: "text-orange-300" },
  pptx: { icon: "fa-solid fa-file-powerpoint", label: "PPTX", iconColor: "text-orange-300" },
  txt: { icon: "fa-solid fa-file-lines", label: "TXT", iconColor: "text-slate-300" },
};

// Derive an extension from a fileType field ("PDF", "application/pdf") or a key/url.
const extOf = (fileType?: string, keyOrUrl?: string): string => {
  const fromType = (fileType || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  if (fromType && STYLES[fromType]) return fromType;
  // try the tail of a mime type, e.g. "application/pdf" -> "pdf"
  const mimeTail = (fileType || "").split("/").pop()?.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (mimeTail && STYLES[mimeTail]) return mimeTail;
  const fromKey = (keyOrUrl || "").split(".").pop()?.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (fromKey && STYLES[fromKey]) return fromKey;
  return "";
};

export const FileTypeThumbnail: React.FC<{
  fileType?: string;
  fileKey?: string;
  fallbackIcon?: string; // category glyph used when the type is unknown
  className?: string;
}> = ({ fileType, fileKey, fallbackIcon, className = "" }) => {
  const ext = extOf(fileType, fileKey);
  const style = ext ? STYLES[ext] : DEFAULT_STYLE;
  const icon = ext ? style.icon : fallbackIcon || DEFAULT_STYLE.icon;

  return (
    <div
      className={`absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br ${TILE_GRADIENT} ${className}`}
    >
      <i className={`${icon} text-5xl ${style.iconColor}`}></i>
      <span className="mt-2 text-xs font-semibold tracking-widest text-white/60">
        {ext ? style.label : ""}
      </span>
    </div>
  );
};

export default FileTypeThumbnail;
